import React, { useState } from 'react';
import dateFormat from 'dateformat';
import { useDispatch, useSelector } from 'react-redux';
import { useDualEventTracking } from '../../../utils/useDualEventTracking';
import { useHistory } from 'react-router-dom';
import { Field, Formik } from 'formik';
import {
  NativeMessageEvent,
  sendNativeMessage,
} from '@carvana/core-native-messaging';
import {
  getLoanDetails,
  getCurrentVehicleId,
  getBankInfo,
  getAllPaymentSchedules,
} from '../../../ducks/selectors';
import CalendarModal from '../../future-packages/calendar-modal';
import ElectronicPaymentTerms from '../../future-packages/electronic-payment-terms-and-conditions';
import OneTimePaymentDialog from '../../future-packages/one-time-payment-confirmation-dialog';
import OneTimeDuplicatePaymentDialog from '../../future-packages/one-time-duplicate-payment-dialog';
import { scheduleOneTimePayment } from '../../../services/MyCarsService';
import {
  DateMasks,
  getISODateString,
  getFormattedDate,
} from '../../../utils/dateUtils';
import {
  ScheduleText,
  ScheduleDate,
  EditLink,
  OneTimePayDetailsWrapper,
  OneTimePayDefaultAmountDueWrapper,
  OneTimePayDefaultAmount,
  OneTimePayDetailsRadio,
  OneTimePayScheduleButton,
  OneTimePayConsentWrapper,
  OneTimePayDetailsCheckbox,
  OneTimePayAmountDueWrapper,
  OneTimePayDetailsInput,
  OneTimePayDateWrapper,
} from './OneTimePaymentForm.styles';
import { AmountDueType } from '../../../constants';
import {
  fetchLoanDetails,
  setOneTimePaymentDetails,
} from '../../../ducks/actions';
import { formatCurrency } from '../../../utils/CurrencyUtils';

import { oneTimePaymentDetailsFormValidationSchema } from './helpers/validationHelper';

const OneTimePaymentForm = () => {
  const environment = process.env.NODE_ENV;
  const { trackEvent } = useDualEventTracking();
  const { cvnaPaymentAmountDue, principalDueDate } = useSelector(
    getLoanDetails
  );
  const vehicleId = useSelector(getCurrentVehicleId);
  const bankInfo = useSelector(getBankInfo)[0];
  const history = useHistory();
  const dispatch = useDispatch();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const currentTime = Date.now(); // Current Local Time in milliseconds

  /**
   * Default Date for payments is current date if principalDueDate is lesser than current date and vice versa
   */
  const initialDate =
    new Date(principalDueDate).getTime() < currentTime
      ? getISODateString(currentTime)
      : principalDueDate;

  // e.g., Thursday, December 08, 2021
  const scheduledDate = getFormattedDate(initialDate);

  const [selectedDate, setSelectedDate] = useState(scheduledDate);

  const [selectedDateInISOFormat, setSelectedDateInISOFormat] = useState(
    initialDate
  ); // Date in payload format

  const consentClicked = () => {
    setIsChecked(!isChecked);
    trackEvent('OneTimePayment Screen - checkbox selected - Tap');
  };

  const [showTermsModal, setShowTermsModal] = useState(false);
  const toggleTermsModal = () => {
    if (!showTermsModal) {
      trackEvent('TermsAndConditions Screen');
      trackEvent('one_time_terms_link_tap');
    } else {
      trackEvent('one_time_payment_tos_dialogue_exit_tap');
    }
    setShowTermsModal(!showTermsModal);
  };

  // Payment Success Modal State and Methods
  const [showSuccessErrorModal, setShowSuccessErrorModal] = useState(false);

  // Duplicate Modal State and Methods
  const allPaymentSchedules = useSelector(getAllPaymentSchedules);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const isDuplicatePayment = (date: string) => {
    /**
     * This replace regex will turn 2017-9-5 into 2017-09-05 and will preserve two digits
     * in dates like 2017-11-11 or 2017-9-05
     */
    const substitute = '$10$2';
    const dateString = date
      .split('T')[0]
      .replace(/(\D)(\d)(?!\d)/g, substitute);
    return allPaymentSchedules.some(
      (schedule: any) => schedule.paymentDate.split('T')[0] === dateString
    );
  };

  // Calendar Modal State and Methods
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const toggleCalendarModal = (e: MouseEvent) => {
    e?.preventDefault();
    if (showCalendarModal === false) {
      trackEvent('one_time_edit_payment_schedule_button_tap');
      trackEvent('Calendar Screen');
    } else {
      trackEvent('one_time_payment_scheduling_date_dialogue_tap');
    }
    setShowCalendarModal(!showCalendarModal);
  };

  // Data for Success/Failure or Duplicate Payment dialog
  const [modalData, setModalData] = useState({
    isError: false,
    errorCode: '',
    errorMessage: '',
    cvnaPaymentAmountDue,
    selectedDate: getFormattedDate(selectedDateInISOFormat, false),
    withdrawalAmount: -1,
    vehicleId,
  });

  const handleDateChange = (newDate: any) => {
    const isoDateString = newDate.concat('T00:00:00Z');
    setSelectedDateInISOFormat(isoDateString);
    // Data for Success/Failure/Duplicate Modal
    setModalData({
      ...modalData,
      ...{
        selectedDate: getFormattedDate(isoDateString, false),
      },
    });
    setSelectedDate(getFormattedDate(newDate));
  };

  // Date options for calendar modal

  const defaultDate = {
    year: parseInt(dateFormat(initialDate, 'yyyy'), 10),
    month: parseInt(dateFormat(initialDate, 'mm'), 10),
    day: parseInt(dateFormat(initialDate, 'dd'), 10),
  };

  const todaysDate = dateFormat(Date.now(), DateMasks.ISOFormat);
  const minimumDate = {
    year: dateFormat(todaysDate, 'yyyy'),
    month: dateFormat(todaysDate, 'mm'),
    day: dateFormat(todaysDate, 'dd'),
  };

  const maxDaysFromToday = 15;
  const dueDatePlusN = dateFormat(
    Date.now() + maxDaysFromToday * 24 * 60 * 60 * 1000,
    DateMasks.ISOFormat
  );

  const maximumDate = {
    year: dateFormat(dueDatePlusN, 'yyyy'),
    month: dateFormat(dueDatePlusN, 'mm'),
    day: dateFormat(dueDatePlusN, 'dd'),
  };

  const dateOptions = {
    defaultDate,
    minimumDate,
    maximumDate,
  };

  // Clear form after submission
  const clearValues = () => {
    setSelectedDate(scheduledDate);
  };

  const toggleSuccessErrorModal = (val: any) => {
    setShowSuccessErrorModal(val);
    const { isError } = modalData;
    if (isError) {
      modalData.isError = false;
      modalData.isLoading = false;
      return;
    }

    dispatch(fetchLoanDetails(process.env.NODE_ENV, vehicleId));
    // Scroll to top
    window.scroll({
      top: 210,
      behavior: 'smooth',
    });
    history.push(`/loan-detail/${vehicleId}`);
  };

  const MinimumPaymentAmount = () => {
    return (
      <OneTimePayDefaultAmount>
        {formatCurrency(cvnaPaymentAmountDue)}
      </OneTimePayDefaultAmount>
    );
  };

  const OneTimePaymentDate = () => {
    return (
      <>
        <ScheduleText>Payment scheduled for</ScheduleText>
        <ScheduleDate>
          {selectedDate}
          <EditLink
            id="edit-calendar"
            href="#"
            role="button"
            tabIndex={0}
            onClick={toggleCalendarModal}
          >
            Edit
          </EditLink>
        </ScheduleDate>
      </>
    );
  };

  const setWithDrawalAmount = (amount: any) => {
    const data = {
      ...modalData,
      withdrawalAmount: amount,
      isLoading: true,
    };
    setModalData(data);
    dispatch(setOneTimePaymentDetails(data));
  };

  const setOneTimePaymentErrors = (errorMessage: any, errorCode: any) => {
    const data = {
      ...modalData,
      isLoading: false,
      isError: true,
      errorCode,
      errorMessage,
    };
    setModalData(data);
    dispatch(setOneTimePaymentDetails(data));
  };

  return (
    <>
      <OneTimePayDetailsWrapper>
        <Formik
          initialValues={{
            paymentAmountDueType: AmountDueType.MINIMUM,
            paymentMinimumAmountDue: cvnaPaymentAmountDue,
            paymentOtherAmountDue: '',
            paymentDate: selectedDate,
            paymentConsent: false,
          }}
          validationSchema={oneTimePaymentDetailsFormValidationSchema}
          onSubmit={(values) => {
            setIsSubmitting(true);
            const withdrawAmount =
              values.paymentAmountDueType === AmountDueType.OTHER
                ? values.paymentOtherAmountDue
                : cvnaPaymentAmountDue;
            setWithDrawalAmount(withdrawAmount);
            if (
              !showDuplicateModal &&
              isDuplicatePayment(selectedDateInISOFormat)
            ) {
              setShowDuplicateModal(true);
              return;
            }
            /**
             * Setting showDuplicateModal to false in order to prevent
             * duplicate pay modal from showing on a loop
             * */
            setShowDuplicateModal(false);

            const { last4OfPaymentMethod } = bankInfo;
            const paymentDetails = {
              last4AccountNumber: last4OfPaymentMethod,
              withdrawScheduleDate: selectedDateInISOFormat, // "2021-09-20T19:04:34.285Z"
              withdrawAmount,
            };
            trackEvent('one_time_schedule_button_tap');
            setShowSuccessErrorModal(true);
            scheduleOneTimePayment(environment, vehicleId, paymentDetails)
              .then(() => {
                setIsSubmitting(false);
                trackEvent('UE_schedule_payment', {
                  vehicleId,
                  scheduleDate: selectedDateInISOFormat,
                  amount: withdrawAmount,
                });
              })
              .catch((error) => {
                trackEvent('one_time_payment_schedule_error', {
                  error: error.response?.data?.message,
                });
                setIsSubmitting(false);
                console.error('Error', error);
                setOneTimePaymentErrors(
                  error.response?.data?.message,
                  error.response?.data?.errorCode
                );
              })
              .finally(() => {
                clearValues();
              });
          }}
          render={({
            touched,
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            errors,
            setFieldValue,
            setErrors,
            setTouched,
          }) => {
            const isSubmitButtonDisabled =
              (Number(values.paymentOtherAmountDue) <= 0 &&
                values.paymentAmountDueType === AmountDueType.OTHER) ||
              errors.paymentOtherAmountDue ||
              (values.paymentAmountDueType &&
                values.paymentDate &&
                values.paymentConsent === false &&
                isSubmitting === false);

            return (
              <form id="one-time-pay-form" onSubmit={handleSubmit}>
                <OneTimePayDefaultAmountDueWrapper>
                  <Field
                    component={OneTimePayDetailsRadio}
                    name="paymentAmountDueType"
                    id="minAmountDue"
                    label="Total Amount Due"
                    touched={values.paymentAmountDueType}
                    error={errors.paymentAmountDueType}
                    value="minimum"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onClick={() => {
                      setFieldValue('paymentOtherAmountDue', '');
                      setErrors({ paymentOtherAmountDue: undefined });
                      setTouched({ paymentOtherAmountDue: undefined });
                    }}
                  />
                  {/* this is static text, no user interaction */}
                  <MinimumPaymentAmount
                    id="paymentMinimumAmount"
                    name="paymentMinimumAmountDue"
                    onChange={handleChange}
                    onBlue={handleBlur}
                    value={values.paymentMinimumAmountDue}
                    touched={touched.paymentMinimumAmountDue}
                    error={errors.paymentMinimumAmountDue}
                  />
                </OneTimePayDefaultAmountDueWrapper>

                <OneTimePayAmountDueWrapper>
                  <Field
                    component={OneTimePayDetailsRadio}
                    name="paymentAmountDueType"
                    id="otherAmountDue"
                    dataTestId="otherAmountDue"
                    label=""
                    touched={values.paymentAmountDueType}
                    error={errors.paymentAmountDueType}
                    value="other"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <OneTimePayDetailsInput
                    id="paymentOtherAmount"
                    inputMode="decimal"
                    disabled={
                      values.paymentAmountDueType === AmountDueType.MINIMUM
                    }
                    mode="light"
                    label="OTHER AMOUNT ($0.00)"
                    name="paymentOtherAmountDue"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onSelect={handleChange}
                    value={values.paymentOtherAmountDue}
                    touched={touched.paymentOtherAmountDue}
                    error={errors.paymentOtherAmountDue}
                  />
                </OneTimePayAmountDueWrapper>

                <OneTimePayDateWrapper>
                  <OneTimePaymentDate
                    id="paymentDate"
                    name="paymentDate"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.paymentDate}
                    touched={touched.paymentDate}
                    error={errors.paymentDate}
                  />
                </OneTimePayDateWrapper>

                <OneTimePayConsentWrapper>
                  <OneTimePayDetailsCheckbox
                    type="checkbox"
                    name="paymentConsent"
                    id="paymentConsent"
                    checked={isChecked}
                    error={errors.paymentConsent}
                    onChange={handleChange}
                    onClick={() => {
                      consentClicked();
                    }}
                  />
                  <div>
                    I acknowledge I have read and agree to the{' '}
                    <a href="#" onClick={toggleTermsModal}>
                      {' '}
                      Bridgecrest Terms and Conditions and Consent for
                      Electronic Signatures and Communications.
                    </a>
                  </div>
                </OneTimePayConsentWrapper>
                <OneTimePayScheduleButton
                  disabled={isSubmitButtonDisabled}
                  type="submit"
                  id="oneTimePayScheduleButton"
                  onClick={() => {
                    sendNativeMessage(
                      'schedule_payment' as NativeMessageEvent,
                      {},
                      'mycars'
                    );
                  }}
                >
                  Schedule Payment
                </OneTimePayScheduleButton>

                {/* One Time Payment Duplicate Payment Modal 
                      included under the <form> element to be able
                      to use "handleSubmit" */}
                <OneTimeDuplicatePaymentDialog
                  modalData={modalData}
                  openModal={showDuplicateModal}
                  toggleDuplicatePaymentModal={setShowDuplicateModal}
                  confirmDuplicatePayment={handleSubmit}
                />
              </form>
            );
          }}
        />
      </OneTimePayDetailsWrapper>

      {/* Calendar Modal */}
      <CalendarModal
        openModal={showCalendarModal}
        toggleCalendarModal={toggleCalendarModal}
        handleDateChange={handleDateChange}
        dateOptions={dateOptions}
      />
      {/* Electronics Terms and Conditions Modal */}
      <ElectronicPaymentTerms
        openModal={showTermsModal}
        toggleTermsModal={toggleTermsModal}
        isOneTimeTerms
      />
      {/* One Time Payment Success/ Failure Modal */}
      <OneTimePaymentDialog
        openModal={showSuccessErrorModal}
        isSubmitting={isSubmitting}
        togglePaymentSuccessErrorModal={toggleSuccessErrorModal}
      />
    </>
  );
};

export default OneTimePaymentForm;
